import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ArrowLeft, Check, Clock, MessageSquare, Pencil, Trash2, UserCheck } from 'lucide-react'
import { SessionTimeRange } from '@/components/SessionClockTime'
import { formatAgendaDayDate } from '@/lib/agendaDayDate'
import { sanitizeRichTextHtml } from '@/lib/sanitizeRichTextHtml'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventUser {
  id: number
  token: string
  firstName: string | null
  lastName: string | null
  phoneNumber: string | null
  email: string | null
  organization: string | null
  position: string | null
  gender: string | null
  country: string | null
  city: string | null
  age: string | null
  title: string | null
  idNumber: string | null
  passportNumber: string | null
  bloodType: string | null
  approved: boolean | null
}

interface CustomField {
  name: string
  value: string | null
}

interface Note {
  id: number
  content: string
  createdAt: string
  createdByEmail: string | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  eventUser: EventUser
  registrationFields: string[]
  customFields: CustomField[]
  selectedSessions: Array<{ id: number; dayNumber: number | null; dayDate?: string | null; time: string; endTime?: string; description: string }>
  notes: Note[]
  hasZone: boolean
  urls: AppShellProps['urls'] & {
    approve: string
    checkIns: string
    eventUsers: string
    notesCreate: string
    notesUpdateBase: string
    notesDeleteBase: string
  }
  translations: AppShellProps['translations'] & {
    userDetails: string
    approve: string
    checkIn: string
    back: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    organization: string
    position: string
    title?: string
    gender: string
    country: string
    city: string
    age: string
    idNumber: string
    passportNumber: string
    bloodType: string
    approved: string
    pending: string
    notes: string
    addNote: string
    editNote: string
    deleteNote: string
    notePlaceholder: string
    selectedSessions: string
    save: string
    cancel: string
    noNotes: string
  }
}

/* ------------------------------------------------------------------ */
/*  Field map                                                          */
/* ------------------------------------------------------------------ */

const FIELD_MAP: Record<string, { key: keyof EventUser; tKey: string }> = {
  'Title':           { key: 'title',          tKey: 'title'          },
  'First name':      { key: 'firstName',      tKey: 'firstName'      },
  'Last name':       { key: 'lastName',       tKey: 'lastName'       },
  'Phone number':    { key: 'phoneNumber',    tKey: 'phoneNumber'    },
  'Email':           { key: 'email',          tKey: 'email'          },
  'Organization':    { key: 'organization',   tKey: 'organization'   },
  'Position':        { key: 'position',       tKey: 'position'       },
  'Gender':          { key: 'gender',         tKey: 'gender'         },
  'Country':         { key: 'country',        tKey: 'country'        },
  'City':            { key: 'city',           tKey: 'city'           },
  'Age':             { key: 'age',            tKey: 'age'            },
  'Id number':       { key: 'idNumber',       tKey: 'idNumber'       },
  'Passport number': { key: 'passportNumber', tKey: 'passportNumber' },
  'Blood type':      { key: 'bloodType',      tKey: 'bloodType'      },
}

function formatNoteDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Details(props: Props) {
  const { event, eventUser, registrationFields, customFields, selectedSessions, notes, hasZone, urls, translations: t, locale, ...shell } = props

  const [newNote, setNewNote] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editContent, setEditContent] = useState('')

  const visibleFields = registrationFields
    .filter((f) => FIELD_MAP[f])
    .map((f) => ({
      label: (t as Record<string, string>)[FIELD_MAP[f].tKey] ?? f,
      value: eventUser[FIELD_MAP[f].key],
    }))
    .filter((f) => f.value)

  const visibleCustom = customFields.filter((f) => f.value)

  function handleApprove() {
    router.visit(urls.approve)
  }

  function handleCheckIn() {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMeta?.getAttribute('content') || ''
    const currentTime = new Date().toLocaleString('en-US', { timeZone: 'Asia/Riyadh' }).replace(', ', 'T')

    fetch(urls.checkIns, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        check_in: { check_in_time: currentTime },
        user_id: eventUser.token,
        zone_id: 1,
      }),
    })
      .then((r) => r.json())
      .then(() => router.reload())
      .catch(() => {})
  }

  function handleAddNote() {
    const content = newNote.trim()
    if (!content) return

    router.post(
      urls.notesCreate,
      { note: { content } },
      { preserveScroll: true, onSuccess: () => setNewNote('') }
    )
  }

  function startEdit(note: Note) {
    setEditingId(note.id)
    setEditContent(note.content)
  }

  function cancelEdit() {
    setEditingId(null)
    setEditContent('')
  }

  function handleUpdateNote(noteId: number) {
    const content = editContent.trim()
    if (!content) return

    router.put(
      urls.notesUpdateBase.replace(':id', String(noteId)),
      { note: { content } },
      { preserveScroll: true, onSuccess: () => cancelEdit() }
    )
  }

  function handleDeleteNote(noteId: number) {
    if (!window.confirm(t.deleteNote + '?')) return

    router.delete(urls.notesDeleteBase.replace(':id', String(noteId)), {
      preserveScroll: true,
    })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.userDetails} — ${event.name}`} />

      <div className="mx-auto max-w-xl py-10 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <div className="flex-1">
              <CardTitle className="text-[#37352F]">
                {[eventUser.firstName, eventUser.lastName].filter(Boolean).join(' ') || '—'}
              </CardTitle>
              <Badge
                variant={eventUser.approved ? 'default' : 'secondary'}
                className={
                  eventUser.approved
                    ? 'mt-1 bg-green-100 text-green-700 hover:bg-green-100'
                    : 'mt-1 bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
                }
              >
                {eventUser.approved ? (
                  <><Check className="mr-1 h-3 w-3" />{t.approved}</>
                ) : (
                  <><Clock className="mr-1 h-3 w-3" />{t.pending}</>
                )}
              </Badge>
            </div>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6 space-y-3">
            {visibleFields.map((f) => (
              <div key={f.label} className="flex items-start gap-2">
                <span className="w-36 shrink-0 text-sm text-[#91918E]">{f.label}</span>
                <span className="text-sm text-[#37352F]">{String(f.value)}</span>
              </div>
            ))}

            {visibleCustom.length > 0 && (
              <>
                <Separator className="my-2" />
                {visibleCustom.map((f) => (
                  <div key={f.name} className="flex items-start gap-2">
                    <span className="w-36 shrink-0 text-sm text-[#91918E]">{f.name}</span>
                    <span className="text-sm text-[#37352F]">{f.value}</span>
                  </div>
                ))}
              </>
            )}

            {selectedSessions.length > 0 && (
              <>
                <Separator className="my-2" />
                <div className="space-y-2">
                  <span className="text-sm font-medium text-[#37352F]">{t.selectedSessions}</span>
                  {selectedSessions.map((session) => {
                    const sessionLocale = locale || 'en'
                    const dayDateLabel = formatAgendaDayDate(session.dayDate, sessionLocale)
                    return (
                    <div key={session.id} className="text-sm text-[#37352F] pl-2 border-l-2 border-[#E8E8E5]">
                      {session.dayNumber != null && <span className="text-[#91918E] mr-2">Day {session.dayNumber}</span>}
                      {dayDateLabel && <span className="text-[#91918E] mr-2">{dayDateLabel}</span>}
                      {(session.time || session.endTime) && (
                        <span className="text-[#91918E] mr-2">
                          <SessionTimeRange start={session.time} end={session.endTime} locale={sessionLocale} />
                        </span>
                      )}
                      <span
                        className="[&_p]:inline [&_p]:m-0"
                        dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(session.description) }}
                      />
                    </div>
                  )})}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#37352F] text-base">
              <MessageSquare className="h-4 w-4" />
              {t.notes}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder={t.notePlaceholder}
                rows={3}
                className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] placeholder:text-[#91918E] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/30"
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  className="bg-[#2383E2] hover:bg-[#1b6ec2]"
                  disabled={!newNote.trim()}
                  onClick={handleAddNote}
                >
                  {t.addNote}
                </Button>
              </div>
            </div>

            {notes.length === 0 ? (
              <p className="text-sm text-[#91918E]">{t.noNotes}</p>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="rounded-lg border border-[#E8E8E5] bg-[#F7F7F5] p-3"
                  >
                    {editingId === note.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          rows={3}
                          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/30"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={cancelEdit}>
                            {t.cancel}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-[#2383E2] hover:bg-[#1b6ec2]"
                            disabled={!editContent.trim()}
                            onClick={() => handleUpdateNote(note.id)}
                          >
                            {t.save}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-[#37352F] whitespace-pre-wrap">{note.content}</p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span className="text-xs text-[#91918E]">
                            {formatNoteDate(note.createdAt)}
                            {note.createdByEmail ? ` · ${note.createdByEmail}` : ''}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-[#91918E] hover:text-[#37352F]"
                              onClick={() => startEdit(note)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-[#91918E] hover:text-red-600"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 flex gap-3 justify-end">
          {!eventUser.approved && (
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
            >
              <Check className="mr-2 h-4 w-4" />
              {t.approve}
            </Button>
          )}

          {eventUser.approved && hasZone && (
            <Button
              className="bg-[#2383E2] hover:bg-[#1b6ec2]"
              onClick={handleCheckIn}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              {t.checkIn}
            </Button>
          )}
        </div>
      </div>
    </AppShell>
  )
}
